import React, { useState, useEffect } from 'react';
import { round3 } from '../../utils/money';
import { IcClose, IcPencil, IcFileText, IcPhone, IcMail, IcMapPin, IcAlertTriangle, IcCreditCard, IcShoppingBag, IcBanknote, IcTrash, IcFacebook, IcInstagram, IcWhatsapp, IcFolderOpen } from '../../theme/icons';
import customerService from '../../services/customerService';
import { gated, GATE_CANCELLED } from '../../services/identityGate';
import saleService from '../../services/saleService';
import authService from '../../services/authService';
import ClientRecordsPanel from './ClientRecordsPanel';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PhoneLink from '../../components/PhoneLink';
import useIsMobile from '../../hooks/useIsMobile';
import { CardSkeleton } from '../../components/Skeleton';

// Social links — click to save a profile/number, or open it when already saved.
const SOCIALS = [
  { key: 'facebook', label: 'Facebook', Icon: IcFacebook, color: 'bg-blue-600', kind: 'profil',
    link: (v) => (v.startsWith('http') ? v : `https://facebook.com/${v}`) },
  { key: 'instagram', label: 'Instagram', Icon: IcInstagram, color: 'bg-rose-600', kind: 'profil',
    link: (v) => (v.startsWith('http') ? v : `https://instagram.com/${v.replace('@', '')}`) },
  { key: 'whatsapp', label: 'WhatsApp', Icon: IcWhatsapp, color: 'bg-green-600', kind: 'numéro',
    link: (v) => `https://wa.me/${v.replace(/\D/g, '')}` },
];

// Each record tab maps to its own service(s): soin by service TYPE, the three
// diagnostics by service NAME keyword (services: "DIAGNOSTIC VISAGE",
// "Diagnostic Cheveux", "POIDS"). `category` is stored on the saved record.
const HISTORY_TABS = [
  { key: 'achat', label: 'Historique', short: 'Achats' },
  { key: 'soin', label: 'Historique soin', short: 'Soin', category: 'SOIN', match: { type: 'SOIN' }, mode: 'none' },
  { key: 'visage', label: 'Diagnostic visage', short: 'Visage', category: 'DIAGNOSTIC', match: { name: 'visage' }, mode: 'link' },
  { key: 'poids', label: 'Poids', short: 'Poids', category: 'POIDS', match: { name: 'poids' }, mode: 'photo' },
  { key: 'cheveux', label: 'Diagnostic cheveux', short: 'Cheveux', category: 'CHEVEUX', match: { name: 'cheveux' }, mode: 'photo' },
];

// Version mobile (Ahmed 2026-08-16, v2) : 2 onglets seulement, SANS
// sous-onglets — Historique montre TOUT (achats + acomptes + soins empilés),
// Diagnostic montre TOUT (visage + poids + cheveux empilés), en listes
// compactes sans défilement horizontal. Web inchangé.
const TAB_GROUPS = [
  { key: 'histo', label: 'Historique' },
  { key: 'diag', label: 'Diagnostic' },
];

// Detail of a single client, shown as a popup over the list. Edit and Delete
// live here (the list no longer has an Actions column or a detail navigation).
const CustomerDetailModal = ({ customerId, onClose, onEdit, onDelete }) => {
  // Sellers can view a client but not edit it. Manager/admin keep full access.
  const canManage = authService.isManagerOrAdmin();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState({
    total_sales_amount: 0,
    paid_amount: 0,
    sales_count: 0,
  });
  const [savingSocial, setSavingSocial] = useState(false);
  const [historyTab, setHistoryTab] = useState('achat');
  // Mobile : 2 onglets tout-en-un (voir TAB_GROUPS).
  const [mobileGroup, setMobileGroup] = useState('histo');
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [acompte, setAcompte] = useState('');
  const [acompteError, setAcompteError] = useState(null);
  const [applyingAcompte, setApplyingAcompte] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const data = await customerService.getCustomerById(customerId);
        if (cancelled) return;
        setCustomerData(data);
        try {
          const salesHistory = await saleService.getCustomerSalesHistory(customerId);
          if (cancelled) return;
          setSalesData(salesHistory.sales || []);
          setPayments(salesHistory.payments || []);
          setStatistics({
            total_sales_amount: salesHistory.statistics?.total_amount || 0,
            paid_amount: salesHistory.statistics?.paid_amount || 0,
            sales_count: salesHistory.statistics?.total_sales || 0,
          });
        } catch (salesErr) {
          console.error('Error fetching sales history:', salesErr);
        }
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching customer details:', err);
        setError('Impossible de charger les détails du client.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (customerId != null) fetchDetails();
    return () => { cancelled = true; };
  }, [customerId]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const formatCurrency = (amount) =>
    `${new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(amount || 0)} DT`;

  // Same rendering but kept as a separate alias: the « DT » unit is separated
  // by a normal (breakable) space, so in the narrow stat tiles on mobile the
  // unit can wrap to its own line instead of pushing the amount out of the
  // card. Grouping spaces stay non-breaking — the number itself never splits.
  const formatAmountWrap = formatCurrency;

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'PPP', { locale: fr });
    } catch (e) {
      return dateString || '-';
    }
  };

  // Date courte pour les listes compactes mobile (moins d'écriture).
  const formatDateShort = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yy');
    } catch (e) {
      return dateString || '-';
    }
  };

  // Save a social profile/number, or open it when already saved.
  const handleSocial = async (s) => {
    const current = (customerData[s.key] || '').trim();
    if (current) {
      const open = window.confirm(`${s.label} : ${current}\n\nOK = ouvrir,  Annuler = modifier`);
      if (open) { window.open(s.link(current), '_blank', 'noopener'); return; }
    }
    const value = window.prompt(`${s.label} — ${s.kind} :`, current);
    if (value === null) return;
    const trimmed = value.trim();
    try {
      setSavingSocial(true);
      const updated = await customerService.patchCustomer(customerData.id, { [s.key]: trimmed || null });
      setCustomerData((d) => ({ ...d, [s.key]: (updated && updated[s.key] !== undefined) ? updated[s.key] : (trimmed || null) }));
    } catch (e) {
      console.error('Error saving social:', e);
      alert("Échec de l'enregistrement.");
    } finally {
      setSavingSocial(false);
    }
  };

  // ----- Acompte: pay down the client's credit across unpaid sales (oldest first),
  // mirroring the supplier facture acompte. -----
  const reloadAfterPayment = async () => {
    const data = await customerService.getCustomerById(customerData.id);
    setCustomerData(data);
    const hist = await saleService.getCustomerSalesHistory(customerData.id);
    setSalesData(hist.sales || []);
    setPayments(hist.payments || []);
    setStatistics({
      total_sales_amount: hist.statistics?.total_amount || 0,
      paid_amount: hist.statistics?.paid_amount || 0,
      sales_count: hist.statistics?.total_sales || 0,
    });
  };

  const handleApplyAcompte = async (e) => {
    e.preventDefault();
    const amount = round3(parseFloat(acompte));
    // Pay against the client's running balance (« reste à payer »), which can
    // include an opening balance not tied to any sale.
    const resteAPayer = Number(customerData?.credit) || 0;
    if (!amount || isNaN(amount) || amount <= 0) {
      setAcompteError('Veuillez entrer un montant valide.');
      return;
    }
    if (amount > resteAPayer + 1e-6) {
      setAcompteError('Le montant dépasse le reste à payer.');
      return;
    }

    setApplyingAcompte(true);
    setAcompteError(null);
    try {
      // Server pays unpaid sales first, then the opening-balance remainder.
      if ((await gated('Qui encaisse ?', () => customerService.payCredit(customerData.id, amount))) === GATE_CANCELLED) return;
      await reloadAfterPayment();
      setShowAcompteModal(false);
      setAcompte('');
    } catch (err) {
      console.error('Acompte error:', err);
      setAcompteError(err?.error || "Erreur lors de l'enregistrement de l'acompte.");
    } finally {
      setApplyingAcompte(false);
    }
  };

  const credit = customerData?.credit || 0;
  const isGold = (customerData?.category_name || '').toLowerCase().includes('gold');

  // Merge ventes + acomptes into one chronological « Historique » (newest first).
  const historyRows = [
    ...salesData.map((s) => ({ kind: 'achat', key: 's' + s.id, dt: s.date_created || s.date_added, s })),
    ...payments.map((p) => ({ kind: 'acompte', key: 'p' + p.id, dt: p.date, p })),
  ].sort((a, b) => new Date(b.dt) - new Date(a.dt));

  return (
    <div
      className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto"
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <CardSkeleton />
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
              <IcAlertTriangle className="h-5 w-5 text-red-400 mr-3" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="mt-4 text-right">
              <button onClick={onClose} className="btn-g px-4 py-2">Fermer</button>
            </div>
          </div>
        ) : customerData ? (
          <>
            {/* Header — entête de modale (Brand Kit §05) : encre 13/16px, titre 600 16px */}
            <div className="modal-head p-6">
              <div className="flex-1 flex items-start justify-between gap-3">
                {/* Left: name + socials + contact */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="modal-title text-xl md:text-2xl break-words min-w-0">{customerData.full_name}</h1>
                    {customerData.category_name && (
                      <span className={`bdg ${isGold ? 'bdg-gold' : 'bdg-fidele'}`}>
                        {customerData.category_name}
                      </span>
                    )}
                    {/* Score + Points au bout de la ligne du nom (avant : colonne de
                        droite avec la ✕). Ainsi la ligne suivante va jusqu'à la
                        colonne ✕, exactement comme dans la fiche produit. */}
                    <span className="ml-auto flex items-center gap-2 shrink-0">
                      {/* Score sticker (smaller) — Studio 2026-08-14 : sur mobile, chiffre seul
                          dans une pilule ambre (fond amber-100, anneau/texte amber-600) ; web inchangé. */}
                      {/* Livre §04 fidélité : « Score N » (amber) + « Points N » (vert plein #16A34A, 11.5px, sans icône). */}
                      <span className="bdg bdg-score rounded-full px-2.5 py-0.5 text-xs tabular-nums">
                        <span className="hidden md:inline">Score </span>{customerData.score || 0}
                      </span>
                      <span className="bdg bdg-points rounded-full px-3 py-1 text-base tabular-nums">
                        <span className="hidden md:inline">Points </span>{Math.round(customerData.points || 0)}
                      </span>
                    </span>
                  </div>

                  {/* Social icons (where the score/points stickers used to be) —
                      et le n° de carte au bout de cette ligne : même place et même
                      habillage que le code de la fiche produit (ligne sous le nom,
                      bout droit, 12px gray-300 font-medium, sans icône). */}
                  <div className="flex items-center gap-2 mt-3">
                    {SOCIALS.map((s) => {
                      const saved = !!(customerData[s.key] && customerData[s.key].trim());
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => handleSocial(s)}
                          disabled={savingSocial}
                          title={saved ? `${s.label} : ${customerData[s.key]} (cliquer pour ouvrir / modifier)` : `Ajouter ${s.label}`}
                          className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${
                            saved ? `${s.color} text-white shadow-sm` : 'bg-white text-gray-400 ring-1 ring-gray-200 hover:text-gray-600 hover:ring-gray-300'
                          } ${savingSocial ? 'opacity-60 cursor-wait' : ''}`}
                        >
                          <s.Icon />
                        </button>
                      );
                    })}
                  </div>

                  {/* Contact */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-gray-300">
                    {customerData.phone && (
                      <span className="inline-flex items-center gap-1.5"><IcPhone className="text-blue-600" size={12} /><PhoneLink phone={customerData.phone} /></span>
                    )}
                    {customerData.email && (
                      <span className="inline-flex items-center gap-1.5 max-w-full"><IcMail className="text-blue-600 shrink-0" size={12} /><span className="break-all">{customerData.email}</span></span>
                    )}
                    {customerData.address && (
                      <span className="inline-flex items-center gap-1.5 max-w-full"><IcMapPin className="text-blue-600 shrink-0" size={12} /><span className="break-words min-w-0">{customerData.address}</span></span>
                    )}
                    {/* N° de carte — sur la ligne du téléphone, bout droit, habillage
                        du code produit (12px gray-300 font-medium) — Ahmed 2026-09-10. */}
                    {customerData.card_number && (
                      <span className="ml-auto text-xs text-gray-300 font-medium whitespace-nowrap">{customerData.card_number}</span>
                    )}
                  </div>
                </div>

                {/* Right: close only — same column as the product panel */}
                <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-full shrink-0" aria-label="Fermer">
                  <IcClose className="text-xl" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Stat cards (compact): left = stacked Total achats + Montant payé, right = Reste à payer.
                  Studio 2026-08-14 : sur mobile les 3 tuiles vivent dans UNE carte blanche
                  (2 colonnes : gauche empilée, droite Reste à payer) ; web inchangé. */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 bg-white border border-gray-200 rounded-2xl shadow-sm p-3 md:bg-transparent md:border-0 md:shadow-none md:p-0 md:rounded-none">
                <div className="flex flex-col gap-3 min-w-0">
                  <div className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 px-3 py-2.5 flex items-center justify-between flex-1 overflow-hidden">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500">Total achats</p>
                      <p className="text-sm md:text-base font-bold text-gray-900 tabular-nums leading-tight">{formatAmountWrap(statistics.total_sales_amount)}</p>
                    </div>
                    <span className="h-7 w-7 rounded-lg hidden md:flex items-center justify-center bg-white text-slate-500 shrink-0"><IcShoppingBag className="text-xs" /></span>
                  </div>
                  {/* Studio 2026-09-08 : même habillage que « Total achats » (neutre),
                      icône coche retirée (et sa pastille blanche vide avec elle). */}
                  <div className="bg-slate-50 rounded-xl shadow-sm border border-slate-200 px-3 py-2.5 flex items-center justify-between flex-1 overflow-hidden">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-500">Montant payé</p>
                      <p className="text-sm md:text-base font-bold text-gray-900 tabular-nums leading-tight">{formatAmountWrap(statistics.paid_amount)}</p>
                    </div>
                  </div>
                </div>

                <div className={`md:col-span-2 min-w-0 rounded-xl shadow-sm border px-3 md:px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3 overflow-hidden ${credit > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`h-8 w-8 rounded-lg flex items-center justify-center bg-white shrink-0 ${credit > 0 ? 'text-red-600' : 'text-green-600'}`}><IcCreditCard /></span>
                      <span className="text-sm font-semibold text-gray-600">Reste à payer</span>
                    </div>
                    <p className={`text-xl md:text-2xl font-bold mt-1 tabular-nums leading-tight ${credit > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatAmountWrap(credit)}</p>
                    {customerData.credit_maximum > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">Crédit max : {formatAmountWrap(customerData.credit_maximum)}</p>
                    )}
                  </div>
                  {credit > 0.0005 && (
                    <button
                      onClick={() => { setAcompte(''); setAcompteError(null); setShowAcompteModal(true); }}
                      className="btn-p shrink-0 px-4 py-2 text-sm transition-colors"
                    >
                      <IcBanknote /> Acompte
                    </button>
                  )}
                </div>
              </div>

              {/* History section with tabs */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-3 py-2 border-b border-gray-100 bg-slate-50 flex flex-wrap items-center gap-1.5">
                  {isMobile ? (
                    TAB_GROUPS.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => setMobileGroup(g.key)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                          mobileGroup === g.key ? 'bg-white border border-gray-900 text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {g.label}
                      </button>
                    ))
                  ) : (
                    HISTORY_TABS.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setHistoryTab(t.key)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          historyTab === t.key ? 'bg-gray-100 border border-gray-500 text-gray-700' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))
                  )}
                  {(isMobile ? mobileGroup === 'histo' : historyTab === 'achat') && (
                    <span className="ml-auto text-xs text-gray-400 pr-1">
                      {statistics.sales_count} vente{statistics.sales_count > 1 ? 's' : ''} au total
                    </span>
                  )}
                </div>

                <div className={`${isMobile ? '' : 'overflow-x-auto'} max-h-96 overflow-y-auto`}>
                  {isMobile ? (
                    mobileGroup === 'histo' ? (
                      <>
                        {/* Achats + acomptes en liste compacte (pas de tableau,
                            pas de défilement horizontal). */}
                        {historyRows.length === 0 ? (
                          <p className="p-6 text-center text-sm text-gray-400">Aucun mouvement.</p>
                        ) : historyRows.map((row) => row.kind === 'acompte' ? (
                          <div key={row.key} className="px-4 py-2.5 border-t border-gray-100 bg-green-50/40 flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-emerald-700 flex items-center gap-1.5"><IcBanknote className="text-xs shrink-0" /> Acompte</p>
                              <p className="text-[11px] text-gray-400">{formatDateShort(row.dt)}</p>
                            </div>
                            <span className="text-sm font-semibold text-emerald-700 tabular-nums shrink-0">− {formatCurrency(row.p.amount || 0)}</span>
                          </div>
                        ) : (
                          <div key={row.key} className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-800">{row.s.is_return ? 'Retour' : 'Achat'} <span className="text-gray-400">#{row.s.id}</span></p>
                              <p className="text-[11px] text-gray-400">
                                {formatDateShort(row.dt)}
                                {row.s.is_return ? (
                                  <span className="text-amber-700 font-semibold"> · Remboursé</span>
                                ) : (row.s.credit === 0 || row.s.amount_payed >= row.s.grand_total) && (
                                  <span className="text-green-600 font-semibold"> · Payé</span>
                                )}
                              </p>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">{formatCurrency(row.s.grand_total || row.s.total_price || 0)}</span>
                            <a
                              href={`/vente/${row.s.id}/receipt`}
                              aria-label="Détails de la vente"
                              className="shrink-0 h-8 w-8 rounded-lg bg-gray-50 text-blue-600 flex items-center justify-center"
                            >
                              <IcFileText className="text-xs" />
                            </a>
                          </div>
                        ))}
                        <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 border-t border-gray-100">Soins</p>
                        <ClientRecordsPanel compact customerId={customerData.id} match={{ type: 'SOIN' }} category="SOIN" label="Historique soin" mode="none" />
                      </>
                    ) : (
                      HISTORY_TABS.filter((t) => ['visage', 'poids', 'cheveux'].includes(t.key)).map((t) => (
                        <div key={t.key}>
                          <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 border-t border-gray-100 first:border-t-0">{t.short}</p>
                          <ClientRecordsPanel compact customerId={customerData.id} match={t.match} category={t.category} label={t.label} mode={t.mode} />
                        </div>
                      ))
                    )
                  ) : historyTab !== 'achat' ? (
                    <ClientRecordsPanel
                      customerId={customerData.id}
                      match={HISTORY_TABS.find((t) => t.key === historyTab)?.match}
                      category={HISTORY_TABS.find((t) => t.key === historyTab)?.category}
                      label={HISTORY_TABS.find((t) => t.key === historyTab)?.label}
                      mode={HISTORY_TABS.find((t) => t.key === historyTab)?.mode}
                    />
                  ) : historyRows.length === 0 ? (
                    <div className="empty-ki m-4">
                      <IcShoppingBag className="mx-auto text-3xl text-gray-200 mb-2" />
                      <p className="text-gray-500">Aucun mouvement pour ce client.</p>
                    </div>
                  ) : (
                    <table className="min-w-full">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant</th>
                          <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                          <th className="px-5 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyRows.map((row) => row.kind === 'acompte' ? (
                          <tr key={row.key} className="border-t border-gray-100 bg-green-50/40">
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(row.dt)}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-emerald-700 inline-flex items-center gap-1.5"><IcBanknote className="text-xs" /> Acompte</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm font-semibold text-emerald-700">− {formatCurrency(row.p.amount || 0)}</td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              <span className="bdg bdg-pos">Payé</span>
                            </td>
                            <td className="px-5 py-3"></td>
                          </tr>
                        ) : (
                          <tr key={row.key} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(row.dt)}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-700">{row.s.is_return ? 'Retour' : 'Achat'} <span className="text-gray-400">#{row.s.id}</span></td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.s.grand_total || row.s.total_price || 0)}</td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              {row.s.is_return ? (
                                <span className="bdg bdg-warn">Retour</span>
                              ) : (row.s.credit === 0 || row.s.amount_payed >= row.s.grand_total) ? (
                                <span className="bdg bdg-pos">Payé</span>
                              ) : (Number(row.s.amount_payed) || 0) > 0 ? (
                                <span className="bdg bdg-alert">Reste {formatCurrency(row.s.credit || 0)}</span>
                              ) : (
                                <span className="bdg bdg-critical">Crédit {formatCurrency(row.s.credit || 0)}</span>
                              )}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <a href={`/vente/${row.s.id}/receipt`} className="font-semibold text-iface-600 hover:text-gray-900 inline-flex items-center justify-end gap-1"><IcFileText className="text-xs" /> Détails</a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {/* Footer — « Fermer » (far left, where the card number was — now in
                the header under Score/Points) opposite the Modifier button (grey).
                Suppression lives inside the edit page now. */}
            {/* Pied de modale (Brand Kit §05) : 11/16px, filet gray-100, gap 8 */}
            <div className="flex justify-between items-center gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className="btn-g py-2.5 px-5 transition-colors shrink-0">
                Fermer
              </button>
              {canManage && (
                <button
                  onClick={() => onEdit(customerData.id)}
                  className="btn-g-edit py-2.5 px-5 transition-colors shrink-0"
                >
                  <IcPencil className="mr-2" /> Modifier
                </button>
              )}
            </div>
          </>
        ) : null}
      </div>

      {/* Acompte modal — mirrors the supplier facture acompte */}
      {showAcompteModal && customerData && (
        <div
          className="modal-backdrop flex items-center justify-center z-[60] p-4"
          onMouseDown={(e) => { e.stopPropagation(); setShowAcompteModal(false); }}
        >
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-head -mx-6 -mt-6 mb-1 rounded-t-2xl">
              <h2 className="modal-title text-lg">Enregistrer un acompte</h2>
              <button onClick={() => setShowAcompteModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <p className="text-sm text-gray-500 mb-5">{customerData.full_name}</p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Reste à payer (total)</span>
                <span className="font-semibold text-red-600">{formatCurrency(credit)}</span>
              </div>
              <p className="text-xs text-gray-400 pt-1">
                L'acompte est appliqué d'abord aux ventes impayées (des plus anciennes aux plus
                récentes), puis au solde restant.
              </p>
            </div>

            <form onSubmit={handleApplyAcompte}>
              {acompteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mt-4">{acompteError}</div>
              )}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-900 mb-1.5">Montant de l'acompte</label>
                <div className="relative">
                  <input
                    type="number"
                    value={acompte}
                    onChange={(e) => setAcompte(e.target.value)}
                    min="0"
                    max={credit}
                    step="0.001"
                    className="inp w-full px-4 py-3 rounded-xl text-base text-gray-900"
                    placeholder="0.000"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">DT</span>
                </div>
              </div>

              {/* Composants-2 §10 : pied à droite — Annuler ghost + Confirmer primaire vert (.btn-p, owner 2026-09-10) */}
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAcompteModal(false)} className="btn-g px-4 py-2.5">Annuler</button>
                <button
                  type="submit"
                  disabled={applyingAcompte}
                  className="btn-p px-6 py-2.5 transition-colors"
                >
                  {applyingAcompte ? 'Enregistrement...' : "Confirmer l'acompte"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetailModal;
